import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService } from '../services/auth-service';
import { LoginCredentials, SignupCredentials, UserWithProfile } from '../types';

interface AuthContextType {
  user: UserWithProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (credentials: LoginCredentials) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<string>;
  signUp: (credentials: SignupCredentials) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-read the user, roles included, after a change made on the server. */
  refreshUser: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserWithProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = authService.onAuthStateChange((initialUser) => {
      setUser(initialUser);
      setIsLoading(false);

      // Hydrate photoURL from /me/ and assistant mode from /assistant-access/session/
      // for the current session. Run in sequence: both rewrite the stored session.
      if (initialUser) {
        void (async () => {
          const withPhoto = await authService.refreshPhotoURL();
          if (!authService.getCurrentUser()) {
            // The session was revoked on the server; show the sign-in page.
            setUser(null);
            return;
          }
          if (withPhoto && withPhoto.photoURL !== initialUser.photoURL) {
            setUser(withPhoto);
          }
          const withActingAs = await authService.refreshActingAs();
          if (
            withActingAs &&
            JSON.stringify(withActingAs.actingAs ?? null) !== JSON.stringify(initialUser.actingAs ?? null)
          ) {
            setUser(withActingAs);
          }
        })();
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (credentials: LoginCredentials) => {
    try {
      setError(null);
      setIsLoading(true);
      const user = await authService.signIn(credentials);
      setUser(user);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      setError(null);
      setIsLoading(true);
      return await authService.requestPasswordReset(email);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (credentials: SignupCredentials) => {
    try {
      setError(null);
      setIsLoading(true);
      const user = await authService.signUp(credentials);
      setUser(user);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const user = await authService.signInWithGoogle();
      setUser(user);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await authService.signOut();
      setUser(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const refreshUser = async () => {
    const refreshed = await authService.refreshUser();
    if (refreshed) setUser(refreshed);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    requestPasswordReset,
    signUp,
    signInWithGoogle,
    signOut,
    refreshUser,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
